import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { components } from "../../api/types";
import {
  clearAmendRefusedAsWaiting,
  useAmendDecision,
  useAmendRefusedAsWaiting,
  useWithdrawChange,
} from "../../hooks/useAmendDecision";
import { useDraft } from "../../hooks/useDraft";
import type { Queue } from "../../hooks/useQueues";
import { useSettleGuard } from "../../hooks/useSettleGuard";
import { useSpecNode } from "../../hooks/useSpec";
import { BAR_LOAD_ERROR_LINE } from "../../lib/nextStep";
import {
  AMEND_REPLACES_LEAD,
  AMEND_REPLACES_TAIL,
  AMEND_SAVED_LINE,
  CROSSING_HEADING,
  NEEDS_HEADING,
  OUT_OF_SCOPE_PREFIX,
  SAVED_ANSWER_LABEL,
  SAVE_AMENDMENT_ACT,
  amendRefusalLine,
  crossingDecisions,
  obligationLabelText,
  provenanceSources,
} from "../../lib/reviewLines";
import { reviewCardHref } from "../../lib/reviewTarget";
import {
  AMEND_ACT,
  AMEND_PLACEHOLDER,
  CHANGE_TITLE_LEAD,
  CLOSE_LABEL,
  DEPENDENTS_HEADING,
  EARLIER_VERSIONS_HEADING,
  REASONING_HEADING,
  REJECTION_REASON_LABEL,
  REVIEW_IT_ACT,
  WITHDRAWN_LINE,
  WITHDRAW_CHANGE_ACT,
  builderDetailHeading,
  changeTarget,
  dateLabel,
  earlierVersionLine,
  entailedLine,
  heldUpLine,
  nodeTitle,
  rowTitle,
  specNodeMap,
  type SpecRow,
  type SpecView,
  type SpecWords,
} from "../../lib/specLines";
import { stripAnchors } from "../../lib/stripAnchors";
import { StateChip } from "./StateChip";

type Node = components["schemas"]["Node"];

// ---- The spec drawer (0051 slice 3) ------------------------------------
//
// One row of the spec read in full, over the column and the map: for a
// decision its commitment, what holds it up, its one act right under them,
// where it came from, the founder's reason on a proposal they turned down,
// its reasoning, its detail (confirmed only), its earlier versions (folded,
// read on demand) and what builds on it; for a question the plain question,
// its one act, its scope lines, the saved answer, what it builds on and what
// builds on it. The words are lib/specLines' and lib/reviewLines'; what each
// row is, is the server's. Every line that names a served row is a button that
// opens it (onOpenRow); anything else is text. The only date is the row's
// served status date, beside the chip.

// Class sets the drawer repeats.
const HEADING = "mb-1 font-mono text-[10px] uppercase tracking-widest text-ink-400";
const PROSE = "break-words whitespace-pre-wrap text-[14px] leading-relaxed text-ink-200";
// The one form for the founder's own words (an answer, a reason).
const QUOTE = "border-l-2 border-ink-700 pl-3 break-words whitespace-pre-wrap text-[14px] leading-relaxed text-ink-200";
const TEXT_LINE = "break-words text-[13px] leading-relaxed text-ink-300";
// A line that opens a row: underlined (never told by colour alone) and, on a
// phone, a 44 px tap target.
const OPEN_ROW =
  "min-h-11 sm:min-h-0 break-words text-left text-[13px] leading-relaxed text-ink-200 underline underline-offset-2 hover:text-ink-50";

function asString(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function fieldsOf(node: Node): Record<string, unknown> {
  return (node.fields ?? {}) as Record<string, unknown>;
}

// commitmentOf is a decision's commitment, by one rule for the row and for
// every earlier version the drawer reads: its value, else its summary
// (anchors collapsed, as in all engine prose).
function commitmentOf(node: Node): string {
  const fields = fieldsOf(node);
  return stripAnchors(asString(fields.value)).trim() || stripAnchors(asString(fields.summary)).trim();
}

// readsAs is a text as the drawer shows it, for comparing two texts: anchors
// stripped, whitespace trimmed and collapsed.
function readsAs(text: string): string {
  return stripAnchors(text).replace(/\s+/g, " ").trim();
}

// waitingChangeTargets maps each decision a saved change is waiting on to the
// carrier it waits on: the trimmed fields.amend_target_decision_id of a served
// question row (a carrier the founder saved, from the drawer or the covered-by
// row) names the decision, and the first such row in row order is its carrier.
// Read from the spec read, never from the drawer's own state, so a waiting
// change shows after any remount: the server refuses a second change to a
// decision whose change still waits on its carrier (planner.AmendCarrierFor;
// 409 amend.same_unserved, 0160), so the drawer never offers Amend while one
// waits, and its withdraw (WITHDRAW_CHANGE_ACT) names that carrier.
function waitingChangeTargets(spec: SpecView): Map<string, string> {
  const out = new Map<string, string>();
  for (const r of spec.rows) {
    if (r.node.type !== "question") continue;
    const target = asString(fieldsOf(r.node).amend_target_decision_id).trim();
    if (target !== "" && !out.has(target)) out.set(target, r.node.id);
  }
  return out;
}

// isOwnAmendCarrier is whether carrierId is one of the decision's own
// synthetic carriers, the ones the server lets the founder withdraw: the first,
// `amend-<decision id>`, or a later one, `amend<n>-<decision id>` with n of 2
// or more (planner.AmendCarrierFor's sequence). A carrier named through the
// API is never withdrawn, so it is never offered WITHDRAW_CHANGE_ACT.
function isOwnAmendCarrier(carrierId: string, decisionId: string): boolean {
  if (decisionId === "") return false;
  if (carrierId === `amend-${decisionId}`) return true;
  const suffix = `-${decisionId}`;
  if (!carrierId.startsWith("amend") || !carrierId.endsWith(suffix)) return false;
  const n = carrierId.slice("amend".length, carrierId.length - suffix.length);
  return /^[1-9][0-9]*$/.test(n) && Number(n) >= 2;
}

// What the bodies share: the served map and its row test, the product's words
// and the two ways out of a row.
type DrawerContext = {
  productId: string;
  row: SpecRow;
  nodeById: ReadonlyMap<string, Node>;
  isRow: (id: string) => boolean;
  words: SpecWords;
  onOpenRow: (id: string) => void;
};

export function SpecDrawer({
  productId,
  spec,
  rowId,
  questions,
  decisions,
  inFlight,
  onOpenRow,
  onClose,
}: {
  productId: string;
  spec: SpecView;
  rowId: string;
  questions?: Queue;
  decisions?: Queue;
  // The page's in-flight signal (a pass is running or the step is working):
  // Amend is held while it holds.
  inFlight: boolean;
  onOpenRow: (id: string) => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const nodeById = useMemo(() => specNodeMap(spec), [spec]);
  const rowIds = useMemo(() => new Set(spec.rows.map((r) => r.node.id)), [spec]);
  const changeWaitingOn = useMemo(() => waitingChangeTargets(spec), [spec]);
  const row = spec.rows.find((r) => r.node.id === rowId);
  const served = row !== undefined;

  // Opening a row is the founder's act, so keyboard focus enters the drawer at
  // its heading, and moves to the new row's heading when a line opens another
  // row (an effect, never the focus-on-mount attribute).
  useEffect(() => {
    if (served) headingRef.current?.focus();
  }, [rowId, served]);

  // Escape closes, as the close button does.
  useEffect(() => {
    if (!served) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [served, onClose]);

  if (row === undefined) return null;

  const node = row.node;
  const ctx: DrawerContext = {
    productId,
    row,
    nodeById,
    isRow: (id) => rowIds.has(id),
    words: spec.meta.product_words,
    onOpenRow,
  };
  // A pending change's head names the decision it changes; when that decision
  // is a row, its quoted title opens it.
  const target = changeTarget(node, nodeById);
  const targetTitle = target === undefined ? "" : stripAnchors(target.title ?? "").trim();
  const headTarget = target !== undefined && targetTitle !== "" && ctx.isRow(target.id) ? target : undefined;
  const date = dateLabel(row.status_set_at);

  return (
    <aside
      role="dialog"
      aria-labelledby={titleId}
      data-testid="spec-drawer"
      className="fixed inset-0 z-40 sm:left-auto sm:w-[440px] overflow-y-auto bg-ink-950 border-l border-ink-800"
    >
      <div className="sticky top-0 z-10 flex items-start gap-2 border-b border-ink-800 bg-ink-950 px-4 py-3 sm:px-5">
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-[16px] font-medium leading-snug text-ink-50 focus:outline-none"
        >
          <span id={titleId} className="min-w-0 break-words">
            {headTarget !== undefined ? (
              <>
                {CHANGE_TITLE_LEAD}"
                <button
                  type="button"
                  onClick={() => onOpenRow(headTarget.id)}
                  className="min-h-11 sm:min-h-0 break-words text-left underline underline-offset-2 hover:text-ink-200"
                >
                  {targetTitle}
                </button>
                "
              </>
            ) : (
              rowTitle(row, nodeById)
            )}
          </span>
          <StateChip state={row.state} />
          {date !== "" && (
            <time dateTime={row.status_set_at} data-testid="drawer-date" className="text-xs text-ink-400 whitespace-nowrap">
              {date}
            </time>
          )}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={CLOSE_LABEL}
          title={CLOSE_LABEL}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-ink-300 hover:text-ink-100 sm:h-7 sm:w-7"
        >
          ✕
        </button>
      </div>
      <div className="space-y-5 px-4 py-4 sm:px-5">
        {node.type === "decision" ? (
          <DecisionBody
            ctx={ctx}
            decisions={decisions}
            inFlight={inFlight}
            waitingCarrier={changeWaitingOn.get(node.id)}
          />
        ) : (
          <QuestionBody ctx={ctx} questions={questions} />
        )}
      </div>
    </aside>
  );
}

// ---- Lines ---------------------------------------------------------------

// titleOf is the title of a node the map holds ("" when it holds none): a row's
// title as its row reads it, so a line never prints a raw id.
function titleOf(ctx: DrawerContext, id: string): string {
  const n = ctx.nodeById.get(id);
  return n === undefined ? "" : nodeTitle(n, ctx.nodeById);
}

// OpenLine is one line naming a node: a button that opens it when it is a row,
// else text.
function OpenLine({ ctx, id, text, testId }: { ctx: DrawerContext; id: string; text: string; testId?: string }) {
  if (ctx.isRow(id)) {
    return (
      <div>
        <button type="button" data-testid={testId} onClick={() => ctx.onOpenRow(id)} className={OPEN_ROW}>
          {text}
        </button>
      </div>
    );
  }
  return (
    <p data-testid={testId} className={TEXT_LINE}>
      {text}
    </p>
  );
}

// Dependents: what builds on this row (the served list), by title.
function Dependents({ ctx }: { ctx: DrawerContext }) {
  const lines = ctx.row.dependents
    .map((id) => ({ id, title: titleOf(ctx, id) }))
    .filter((l) => l.title !== "");
  if (lines.length === 0) return null;
  return (
    <section>
      <p className={HEADING}>{DEPENDENTS_HEADING}</p>
      {lines.map((l) => (
        <OpenLine key={l.id} ctx={ctx} id={l.id} text={l.title} />
      ))}
    </section>
  );
}

// ---- A decision -------------------------------------------------------------

function DecisionBody({
  ctx,
  decisions,
  inFlight,
  waitingCarrier,
}: {
  ctx: DrawerContext;
  decisions?: Queue;
  inFlight: boolean;
  // The carrier of the founder's waiting change when a served question row
  // names this decision as its amend target: the saved line then stands where
  // Amend would be.
  waitingCarrier: string | undefined;
}) {
  const { row, nodeById, productId } = ctx;
  const node = row.node;
  const fields = fieldsOf(node);
  const status = node.status;

  const value = commitmentOf(node);
  const heldUp = row.blocked_by
    .map((id) => ({ id, title: titleOf(ctx, id) }))
    .filter((l) => l.title !== "");
  const reason = row.state === "rejected" ? asString(fields.rejection_reason) : "";

  // Where it came from: the decisions a confirmed one follows from, then the
  // allowlist's lines (Replaces only while it waits: a turned-down or confirmed
  // decision's supersedes refs are never shown as Replaces).
  const sources = provenanceSources(node, nodeById);
  const entailed: { id: string; line: string }[] = [];
  if (status === "confirmed") {
    for (const ref of node.refs ?? []) {
      if (ref.rel !== "entailed-by" || entailed.some((l) => l.id === ref.to)) continue;
      const parent = nodeById.get(ref.to);
      if (parent === undefined || parent.type !== "decision") continue;
      const title = nodeTitle(parent, nodeById);
      if (title !== "") entailed.push({ id: ref.to, line: entailedLine(title) });
    }
  }
  const replaces = status === "proposed" ? sources.replaces : [];
  const hasProvenance =
    entailed.length + sources.fromAnswer.length + replaces.length + sources.buildsOn.length > 0;

  const reasoning = stripAnchors(asString(fields.reasoning_chain)).trim();
  const artifacts = status === "confirmed" && Array.isArray(fields.artifacts) ? artifactTexts(fields.artifacts) : [];

  // Earlier versions (a confirmed decision only): each supersedes target the
  // served context holds as a decision that is not a row, by title.
  const earlier: Node[] = [];
  if (status === "confirmed") {
    for (const ref of node.refs ?? []) {
      if (ref.rel !== "supersedes" || ctx.isRow(ref.to) || earlier.some((n) => n.id === ref.to)) continue;
      const version = nodeById.get(ref.to);
      if (version === undefined || version.type !== "decision") continue;
      if (stripAnchors(version.title ?? "").trim() === "") continue;
      earlier.push(version);
    }
  }

  // A confirmed decision's act is Amend, unless a change to it already waits
  // (ConfirmedAct, keyed by the decision). The waiting change's text, from the
  // spec read, is what a withdraw hands back to Amend.
  const waitingNode = waitingCarrier === undefined ? undefined : nodeById.get(waitingCarrier);
  const waitingText = waitingNode === undefined ? "" : asString(fieldsOf(waitingNode).answer);
  const act =
    status === "confirmed" ? (
      <ConfirmedAct
        key={node.id}
        productId={productId}
        node={node}
        inFlight={inFlight}
        waitingCarrier={waitingCarrier}
        waitingText={waitingText}
      />
    ) : status === "proposed" ? (
      <Link
        to={reviewCardHref(productId, "decisions", decisions, (id) => id === node.id)}
        className="inline-flex min-h-11 items-center rounded-md bg-accent px-3.5 py-1.5 text-sm font-medium text-ink-950 transition-colors hover:bg-accent-glow sm:min-h-0"
      >
        {REVIEW_IT_ACT}
      </Link>
    ) : null;

  return (
    <>
      {value !== "" && (
        <p data-testid="drawer-value" className={PROSE}>
          {value}
        </p>
      )}

      {heldUp.length > 0 && (
        <section>
          {heldUp.map((l) => (
            <OpenLine key={l.id} ctx={ctx} id={l.id} text={heldUpLine(l.title)} />
          ))}
        </section>
      )}

      {act !== null && <div data-testid="drawer-act">{act}</div>}

      {reason.trim() !== "" && (
        <section>
          <p className={HEADING}>{REJECTION_REASON_LABEL}</p>
          <p data-testid="drawer-rejected-reason" className={QUOTE}>
            {reason}
          </p>
        </section>
      )}

      {hasProvenance && (
        <section data-testid="drawer-provenance" className="space-y-1">
          {entailed.map((l) => (
            <OpenLine key={`entailed-${l.id}`} ctx={ctx} id={l.id} text={l.line} testId="drawer-entailed" />
          ))}
          {sources.fromAnswer.map((s) => {
            // The founder's own words, as stored; when they read as the
            // commitment shown above, the line alone (0051 slice 3, ruled
            // 2026-10-14: the same words twice say nothing more).
            const source = nodeById.get(s.id);
            const answer = source === undefined ? "" : asString(fieldsOf(source).answer);
            const quoted = answer.trim() !== "" && readsAs(answer) !== readsAs(value);
            return (
              <div key={`from-${s.id}`} className="space-y-1">
                <OpenLine ctx={ctx} id={s.id} text={s.line} />
                {quoted && <p className={QUOTE}>{answer}</p>}
              </div>
            );
          })}
          {replaces.map((s) => (
            <OpenLine key={`replaces-${s.id}`} ctx={ctx} id={s.id} text={s.line} />
          ))}
          {sources.buildsOn.map((s) => (
            <OpenLine key={`builds-${s.id}`} ctx={ctx} id={s.id} text={s.line} />
          ))}
        </section>
      )}

      {reasoning !== "" && (
        <section>
          <p className={HEADING}>{REASONING_HEADING}</p>
          <p className={PROSE}>{reasoning}</p>
        </section>
      )}

      {artifacts.length > 0 && (
        <section className="space-y-2">
          <p className={HEADING}>{builderDetailHeading(ctx.words)}</p>
          {artifacts.map((text, i) => (
            <pre
              key={i}
              data-testid="drawer-artifact"
              className="whitespace-pre-wrap break-words rounded-md border border-ink-800 bg-ink-900/40 px-3 py-2 font-mono text-[12px] leading-relaxed text-ink-200"
            >
              {text}
            </pre>
          ))}
        </section>
      )}

      {earlier.length > 0 && (
        <section className="space-y-1">
          <p className={HEADING}>{EARLIER_VERSIONS_HEADING}</p>
          {earlier.map((version) => (
            <EarlierVersion
              key={version.id}
              productId={productId}
              id={version.id}
              title={stripAnchors(version.title ?? "").trim()}
              isRow={ctx.isRow}
              chain={new Set([node.id])}
            />
          ))}
        </section>
      )}

      <Dependents ctx={ctx} />
    </>
  );
}

// artifactTexts is each artifact's content: a string as written, anchors
// collapsed as in all engine prose; any other value as JSON. An entry with no
// content is left out, and its engine type label is never shown.
function artifactTexts(entries: unknown[]): string[] {
  const out: string[] = [];
  for (const entry of entries) {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) continue;
    const content = (entry as Record<string, unknown>).content;
    if (typeof content === "string") {
      const text = stripAnchors(content);
      if (text.trim() !== "") out.push(text);
    } else if (content !== undefined && content !== null) {
      out.push(JSON.stringify(content));
    }
  }
  return out;
}

// ---- The Amend block (a confirmed decision) ---------------------------------

// AmendSavedLine is the ruled line for a change that waits: from the spec read
// (a served question row names the decision), or the block's own saved state
// until the spec read shows it.
function AmendSavedLine() {
  return (
    <p data-testid="drawer-amend-saved" className="text-[14px] leading-relaxed text-ink-200">
      {AMEND_SAVED_LINE}
    </p>
  );
}

// ConfirmedAct is a confirmed decision's act, mounted keyed by the decision, so
// its state is that decision's own and goes when the drawer closes or shows
// another node. Amend, unless a change to it already waits: then the saved
// line, with no Amend and no box (the block does not mount, so it cannot reload
// an old draft and write it back).
//
// 0160 (RULED 2026-10-15): a spec read older than a change the founder saved
// elsewhere (another tab) still offers Amend, and the server refuses that
// second change (409 amend.same_unserved). Then the refusal's ruled line stands
// above the saved line, with no Amend and no box: at once, and after the spec
// read refetches and its waiting signal takes over, until the drawer closes or
// shows another node. The refusal is read from useAmendDecision's record, never
// from this body's own state alone: the 409 can land after this body unmounted
// (the drawer closed, or showed another node, while the save was pending), and
// the founder must still see it on this decision's next mount. Once shown, it
// stays for this mount until this mount withdraws the change, and the record is
// cleared when the mount goes. The refused text is not kept.
//
// 0160 (RULED 2026-10-20): a waiting change on one of the decision's own
// carriers (isOwnAmendCarrier) offers WITHDRAW_CHANGE_ACT under its saved line
// (WaitingChange). On its 2xx, for this mount, WITHDRAWN_LINE stands in the act
// slot and Amend returns, focused, its box holding the withdrawn text (read
// from the spec read at the tap), so replacing a waiting change is withdraw,
// edit, save. That state comes before the refusal (a 2xx clears the refusal
// and its record): while the read still names the withdrawn carrier, or none,
// the line and Amend stay; once it names a DIFFERENT waiting carrier (the
// founder saved the replacement, or another tab did), its saved line and its
// own WITHDRAW_CHANGE_ACT show. A refusal recorded after the withdraw (the
// replacement's save refused because another page's change to the decision
// now waits) ends that state: the refusal's line stands above the saved line,
// as on any refusal, never the saved line alone. A 2xx that lands after this
// mount went shows nothing here: the change is withdrawn as asked.
function ConfirmedAct({
  productId,
  node,
  inFlight,
  waitingCarrier,
  waitingText,
}: {
  productId: string;
  node: Node;
  inFlight: boolean;
  // The carrier the spec read serves the decision's waiting change on, if any.
  waitingCarrier: string | undefined;
  // That change's text, as the spec read serves it.
  waitingText: string;
}) {
  const recorded = useAmendRefusedAsWaiting(productId, node.id);
  // The change this mount withdrew (its carrier and text), and whether its
  // replacement has since been saved from the block below the after-line.
  const [withdrawn, setWithdrawn] = useState<{ carrier: string; text: string } | null>(null);
  const [replaced, setReplaced] = useState(false);
  // Shown for this mount once the record is read (at mount, or when the 409
  // lands while mounted). A refusal recorded after this mount's withdraw (the
  // replacement's save refused: another page's change now waits) ends the
  // withdrawn state, so its line stands above the saved line, never the saved
  // line alone. The withdraw clears any earlier record before its state is set
  // (onWithdrawn), so only a later refusal ends it.
  const [refusedAsWaiting, setRefusedAsWaiting] = useState(recorded);
  if (recorded && !refusedAsWaiting) {
    setRefusedAsWaiting(true);
    setWithdrawn(null);
  }
  // Cleared only after it has been shown: when this mount goes (the drawer
  // closes or shows another node).
  useEffect(() => {
    if (!refusedAsWaiting) return;
    return () => clearAmendRefusedAsWaiting(productId, node.id);
  }, [refusedAsWaiting, productId, node.id]);
  // The change a tap asked to withdraw, read when its 2xx lands.
  const asked = useRef<{ carrier: string; text: string } | null>(null);
  const { withdraw, errorLine } = useWithdrawChange(productId, {
    onWithdrawn: () => {
      if (asked.current !== null) setWithdrawn(asked.current);
      setReplaced(false);
      setRefusedAsWaiting(false);
      clearAmendRefusedAsWaiting(productId, node.id);
    },
    // The drawer shows its own in-flight signal: the link is held, with its line.
    ownInFlightSignal: true,
  });
  const onWithdraw = (carrier: string) => {
    if (withdraw.isPending) return;
    asked.current = { carrier, text: waitingText };
    withdraw.mutate({ decisionId: node.id, carrierQuestionId: carrier });
  };
  const waiting = (
    <WaitingChange
      carrier={waitingCarrier}
      decisionId={node.id}
      inFlight={inFlight}
      pending={withdraw.isPending}
      errorLine={errorLine}
      onWithdraw={onWithdraw}
    />
  );

  if (withdrawn !== null) {
    if (waitingCarrier !== undefined && waitingCarrier !== withdrawn.carrier) return waiting;
    return (
      <>
        {!replaced && (
          <p
            role="status"
            data-testid="drawer-withdrawn"
            className="mb-2 break-words text-[14px] leading-relaxed text-ink-200"
          >
            {WITHDRAWN_LINE}
          </p>
        )}
        <AmendBlock
          key={node.id}
          productId={productId}
          node={node}
          inFlight={inFlight}
          initialText={withdrawn.text}
          focusAmend
          onSaved={() => setReplaced(true)}
        />
      </>
    );
  }
  if (refusedAsWaiting) {
    return (
      <>
        <p
          role="status"
          data-testid="drawer-amend-already-waiting"
          className="mb-1 break-words text-[14px] leading-relaxed text-ink-100"
        >
          {amendRefusalLine("amend.same_unserved")}
        </p>
        {waiting}
      </>
    );
  }
  if (waitingCarrier !== undefined) return waiting;
  return <AmendBlock key={node.id} productId={productId} node={node} inFlight={inFlight} />;
}

// WaitingChange is a waiting change's saved line, then, when the spec read
// names the carrier it waits on and that carrier is one of the decision's own
// (isOwnAmendCarrier), WITHDRAW_CHANGE_ACT: a link, a phone-size target, held
// while the page's pass is in flight (with the amend's in-flight line) or the
// withdraw is out, with the withdraw's failure line under it.
function WaitingChange({
  carrier,
  decisionId,
  inFlight,
  pending,
  errorLine,
  onWithdraw,
}: {
  carrier: string | undefined;
  decisionId: string;
  inFlight: boolean;
  pending: boolean;
  errorLine: string | null;
  onWithdraw: (carrier: string) => void;
}) {
  return (
    <>
      <AmendSavedLine />
      {carrier !== undefined && isOwnAmendCarrier(carrier, decisionId) && (
        <WithdrawLink
          carrier={carrier}
          inFlight={inFlight}
          pending={pending}
          errorLine={errorLine}
          onWithdraw={() => onWithdraw(carrier)}
        />
      )}
    </>
  );
}

// WithdrawLink is WITHDRAW_CHANGE_ACT, held by the one settle time of every
// one-tap act (founder ruling of 2026-10-19; useSettleGuard, ACT_SETTLE_MS): it
// takes no tap or key for the settle time after it appears (the refetch that
// names the carrier pushes the drawer's content down under a finger) or after
// its carrier changes (resetKey), and never the click that ends a press it did
// not see begin. Nothing on screen says so.
function WithdrawLink({
  carrier,
  inFlight,
  pending,
  errorLine,
  onWithdraw,
}: {
  carrier: string;
  inFlight: boolean;
  pending: boolean;
  errorLine: string | null;
  onWithdraw: () => void;
}) {
  const guard = useSettleGuard({ resetKey: carrier });
  return (
    <div className="mt-1">
      <button
        type="button"
        data-testid="drawer-withdraw"
        disabled={inFlight || pending}
        onClick={() => guard.onClick(onWithdraw)}
        onPointerDown={guard.onPointerDown}
        onPointerUp={guard.onPointerUp}
        onPointerCancel={guard.onPointerCancel}
        onKeyDown={guard.onKeyDown}
        className="min-h-11 sm:min-h-0 break-words text-left text-[13px] leading-relaxed text-ink-300 underline underline-offset-2 hover:text-ink-100 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {WITHDRAW_CHANGE_ACT}
      </button>
      {inFlight && (
        <p role="status" className="mt-2 break-words text-[13px] leading-relaxed text-ink-400">
          {amendRefusalLine("amend.run_in_flight")}
        </p>
      )}
      {errorLine && (
        <p role="alert" className="mt-2 break-words text-sm text-danger">
          {errorLine}
        </p>
      )}
    </div>
  );
}

// Mounted keyed by the decision, and only while no change to it waits:
// useDraft reads its storage key only at mount, and the drawer switches rows
// in place, so an unkeyed block would offer one decision's draft in another's
// box and post it for that one. The key also gives each decision its own open
// and saved state. The saved state covers only the moment between the POST's
// 2xx and the spec read's refresh: once the read serves the change's carrier,
// the drawer shows the same line in place of the block, after any remount.
// After this mount withdrew a change (ConfirmedAct), the block holds the
// withdrawn text as its draft's initial value, a prefill for this mount only
// (useDraft's persistInitial: false: never stored as a draft until the founder
// edits it; a cached draft wins), its Amend takes focus once (the tapped link
// is gone), and onSaved tells ConfirmedAct the replacement was saved.
function AmendBlock({
  productId,
  node,
  inFlight,
  initialText = "",
  focusAmend = false,
  onSaved,
}: {
  productId: string;
  node: Node;
  inFlight: boolean;
  initialText?: string;
  focusAmend?: boolean;
  onSaved?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [text, setText, clearDraft] = useDraft(productId, node.id + "-drawer-amend", initialText, {
    persistInitial: false,
  });
  const { amend, errorLine } = useAmendDecision(productId, {
    onSaved: () => {
      clearDraft();
      setSaved(true);
      onSaved?.();
    },
    // 0160: a change to this decision already waits. The hook records the
    // refusal and ConfirmedAct shows it: at once when it lands while the
    // drawer still shows this decision (ending a withdrawn state this block
    // was shown in), or on the decision's next mount when it lands after the
    // drawer closed or moved. The founder's refused text is not kept: the
    // founder replaces a waiting change through WITHDRAW_CHANGE_ACT, then
    // Amend.
    onAlreadyWaiting: clearDraft,
    // The drawer shows its own in-flight signal: Amend is held, with its line.
    ownInFlightSignal: true,
  });
  // Amend takes focus once when the block mounts after a withdraw (an explicit
  // act whose link just unmounted), never on an ordinary mount.
  const amendRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusAmend) amendRef.current?.focus();
  }, [focusAmend]);
  // The box takes focus when the founder opens the block (an explicit act),
  // never on mount.
  const boxRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (open) boxRef.current?.focus();
  }, [open]);

  if (saved) return <AmendSavedLine />;

  const trimmed = text.trim();
  const submit = () => {
    if (trimmed === "" || amend.isPending) return;
    amend.mutate({ decisionId: node.id, answerText: trimmed });
  };

  return (
    <div>
      <button
        ref={amendRef}
        type="button"
        aria-expanded={open}
        disabled={inFlight}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex min-h-11 items-center rounded-md border border-ink-700 px-3.5 py-1.5 text-sm font-medium text-ink-100 transition-colors hover:border-ink-500 disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
      >
        {AMEND_ACT}
      </button>
      {inFlight && (
        <p role="status" className="mt-2 break-words text-[13px] leading-relaxed text-ink-400">
          {amendRefusalLine("amend.run_in_flight")}
        </p>
      )}
      {open && !inFlight && (
        <div className="mt-3">
          <p className="break-words text-[13px] leading-relaxed text-ink-300">
            {AMEND_REPLACES_LEAD}
            <strong className="font-medium text-ink-100">"{stripAnchors(node.title ?? "").trim()}"</strong>
            {AMEND_REPLACES_TAIL}
          </p>
          <div className="mt-3 rounded-lg border border-ink-700 bg-ink-900/60 transition-colors hover:border-ink-600 focus-within:border-accent/60">
            <textarea
              ref={boxRef}
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={AMEND_PLACEHOLDER}
              aria-label={AMEND_PLACEHOLDER}
              // readOnly, never disabled, while the save is out: a disabled
              // focused box loses focus.
              readOnly={amend.isPending}
              className="w-full resize-none bg-transparent px-4 py-3 text-sm text-ink-100 placeholder:text-ink-500 focus:outline-none"
            />
            <div className="flex items-center justify-end border-t border-ink-800 px-3 py-2">
              <button
                type="button"
                onClick={submit}
                disabled={trimmed === "" || amend.isPending}
                className="min-h-11 rounded-md bg-accent px-3 py-1 text-xs font-medium text-ink-950 transition-colors hover:bg-accent-glow disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
              >
                {amend.isPending ? "saving…" : SAVE_AMENDMENT_ACT}
              </button>
            </div>
          </div>
          {errorLine && (
            <p role="alert" className="mt-3 text-sm text-danger">
              {errorLine}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Earlier versions (read on demand) -----------------------------------
//
// A closed fold per earlier version, by title (undated). Its first open reads
// the version once (GET /nodes/{nid}) and shows its full text, by the row's
// own commitment rule (commitmentOf: value, else summary); once read, and
// while it is open, each of its own supersedes targets that is not a served
// row is read too, and its fold appears only once that read has returned the
// node, so no fold ever shows a raw id. `chain` holds the decision and the
// versions above this one, so a cycle of supersedes refs never nests.
function EarlierVersion({
  productId,
  id,
  title,
  isRow,
  chain,
  loaded,
}: {
  productId: string;
  id: string;
  title: string;
  isRow: (id: string) => boolean;
  chain: ReadonlySet<string>;
  // The version's node when a nested read already returned it.
  loaded?: Node;
}) {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const read = useSpecNode(productId, opened && loaded === undefined ? id : null);
  const version = loaded ?? read.data;

  let body = null;
  if (opened) {
    if (version !== undefined) {
      const value = commitmentOf(version);
      const below = new Set([...chain, id]);
      const older: string[] = [];
      for (const ref of version.refs ?? []) {
        if (ref.rel !== "supersedes" || isRow(ref.to) || below.has(ref.to) || older.includes(ref.to)) continue;
        older.push(ref.to);
      }
      body = (
        <div className="mt-2 space-y-2 border-l-2 border-ink-800 pl-3">
          {value !== "" && <p className={PROSE}>{value}</p>}
          {open &&
            older.map((olderId) => (
              <OlderVersion key={olderId} productId={productId} id={olderId} isRow={isRow} chain={below} />
            ))}
        </div>
      );
    } else if (read.isError) {
      body = <p className={`mt-2 ${TEXT_LINE}`}>{BAR_LOAD_ERROR_LINE}</p>;
    } else {
      body = <p className={`mt-2 ${TEXT_LINE}`}>loading…</p>;
    }
  }

  return (
    <details open={open} className="min-w-0">
      <summary
        onClick={(e) => {
          // The fold is the drawer's state, so a test and every browser agree.
          e.preventDefault();
          setOpen((o) => !o);
          setOpened(true);
        }}
        className="min-h-11 cursor-pointer break-words text-[13px] leading-relaxed text-ink-300 hover:text-ink-100 sm:min-h-0"
      >
        {earlierVersionLine(title)}
      </summary>
      {body}
    </details>
  );
}

// OlderVersion reads one version below an open one and renders its fold only
// once the read has returned a titled node.
function OlderVersion({
  productId,
  id,
  isRow,
  chain,
}: {
  productId: string;
  id: string;
  isRow: (id: string) => boolean;
  chain: ReadonlySet<string>;
}) {
  const read = useSpecNode(productId, id);
  const version = read.data;
  if (version === undefined || version.type !== "decision") return null;
  const title = stripAnchors(version.title ?? "").trim();
  if (title === "") return null;
  return <EarlierVersion productId={productId} id={id} title={title} isRow={isRow} chain={chain} loaded={version} />;
}

// ---- A question -------------------------------------------------------------

function QuestionBody({ ctx, questions }: { ctx: DrawerContext; questions?: Queue }) {
  const { row, nodeById, productId } = ctx;
  const node = row.node;
  const fields = fieldsOf(node);

  const question = stripAnchors(asString(fields.question)).trim() || stripAnchors(asString(fields.summary)).trim();
  // Review's scope gate exactly: the out-of-scope line and the decisions that
  // cross it only on a scope question.
  const scopeLine =
    asString(fields.authority_conformance_dimension) === "scope"
      ? stripAnchors(asString(fields.out_of_scope_entry)).trim()
      : "";
  const crossing = scopeLine !== "" ? crossingDecisions(node, nodeById) : [];
  const crossingHeading = obligationLabelText(node) !== "" ? NEEDS_HEADING : CROSSING_HEADING;
  const savedAnswer = asString(fields.answer);
  // What it builds on, less the crossing decisions (listed above) and the
  // decision a pending change changes (its head names it).
  const amendTarget = asString(fields.amend_target_decision_id).trim();
  const omit = new Set([...crossing.map((c) => c.id), ...(amendTarget !== "" ? [amendTarget] : [])]);
  const buildsOn = provenanceSources(node, nodeById, omit).buildsOn;

  return (
    <>
      {question !== "" && (
        <p data-testid="drawer-question" className={PROSE}>
          {question}
        </p>
      )}

      <div data-testid="drawer-act">
        <Link
          to={reviewCardHref(productId, "questions", questions, (id) => id === node.id)}
          className="inline-flex min-h-11 items-center rounded-md bg-accent px-3.5 py-1.5 text-sm font-medium text-ink-950 transition-colors hover:bg-accent-glow sm:min-h-0"
        >
          {REVIEW_IT_ACT}
        </Link>
      </div>

      {scopeLine !== "" && (
        <p className={TEXT_LINE}>
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-500">{OUT_OF_SCOPE_PREFIX}</span>
          {scopeLine}
        </p>
      )}

      {crossing.length > 0 && (
        <section>
          <p className={HEADING}>{crossingHeading}</p>
          {crossing.map((c) => (
            <OpenLine key={c.id} ctx={ctx} id={c.id} text={c.title} />
          ))}
        </section>
      )}

      {savedAnswer.trim() !== "" && (
        <section>
          <p className={HEADING}>{SAVED_ANSWER_LABEL}</p>
          <p data-testid="drawer-saved-answer" className={QUOTE}>
            {savedAnswer}
          </p>
        </section>
      )}

      {buildsOn.length > 0 && (
        <section data-testid="drawer-provenance" className="space-y-1">
          {buildsOn.map((s) => (
            <OpenLine key={s.id} ctx={ctx} id={s.id} text={s.line} />
          ))}
        </section>
      )}

      <Dependents ctx={ctx} />
    </>
  );
}
